import { useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import SalesLayout from '../../components/SalesLayout';
import AuthService from '../../services/authService';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function Profile({ pageTitle, section }) {
    const stored = AuthService.getUser();
    const superAdmin = stored?.superAdmin;
    const staff = superAdmin?.staff || stored?.staff || null;

    const isSuperAdmin = !!superAdmin?.superAdminId || superAdmin?.staff?.type === 'admin';

    const [name, setName] = useState(staff?.name || '');
    const [username, setUsername] = useState(superAdmin?.username || '');
    const [officeEmail, setOfficeEmail] = useState(staff?.officeEmail || '');
    const [gmail, setGmail] = useState(staff?.gmail || '');
    const [mobileNumber, setMobileNumber] = useState(staff?.mobileNumber || '');
    const [gender, setGender] = useState(staff?.gender || '');
    const [title, setTitle] = useState(staff?.title || '');
    const [rosters, setRosters] = useState(staff?.rosters || []);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const fileInputRef = useRef(null);

    const [avatarUrl, setAvatarUrl] = useState(undefined);
    const [avatarLoading, setAvatarLoading] = useState(false);

    const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
    const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

    useEffect(() => {
        try {
            const imagePath = staff?.imagePath;
            if (!imagePath) {
                setAvatarUrl(undefined);
                setAvatarLoading(false);
                return;
            }
            setAvatarUrl(getImageURL(imagePath));
            setAvatarLoading(false);
        } catch (error) {
            console.error('Sales Profile: avatar load failed', error);
            setAvatarUrl(undefined);
            setAvatarLoading(false);
        }
    }, [staff?.imagePath]);

    // Load existing roster for logged-in staff on mount
    useEffect(() => {
        const loadRoster = async () => {
            try {
                const resp = await axiosInstance.get('/admin/lecturer/rosters');
                if (resp.data?.success && Array.isArray(resp.data.rosters)) {
                    setRosters(resp.data.rosters);
                }
            } catch (error) {
                console.error('Failed to load staff roster for sales profile:', error);
            }
        };

        loadRoster();
    }, []);

    const handleImageUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid File',
                text: 'Please select an image file (JPEG, PNG, etc.)',
            });
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            Swal.fire({
                icon: 'error',
                title: 'File Too Large',
                text: 'Please select an image smaller than 5MB',
            });
            return;
        }

        if (!staff?.staffId) return;
        try {
            Swal.fire({
                title: 'Uploading...',
                text: 'Please wait while we upload the image',
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading(),
            });

            const form = new FormData();
            form.append('image', file);

            const resp = await axiosInstance.post('/admin/lecturer/profile/image', form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (!resp.data?.success) {
                throw new Error(resp.data?.message || 'Failed to upload image');
            }

            const newPath = resp.data.filename || resp.data.imagePath;
            if (newPath) {
                window.dispatchEvent(
                    new CustomEvent('ka-user-avatar-updated', {
                        detail: { staffId: staff.staffId, filename: newPath },
                    })
                );
            }

            Swal.fire({ icon: 'success', title: 'Success!', text: resp.data?.message || 'Profile image uploaded successfully', timer: 1800 });
            // Reload to pick up updated imagePath from backend
            setTimeout(() => window.location.reload(), 600);
        } catch (error) {
            console.error('Error uploading image:', error);
            Swal.fire({ icon: 'error', title: 'Upload Failed', text: error.message || 'Failed to upload image. Please try again.' });
        } finally {
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleProfileUpdate = async (event) => {
        event.preventDefault();

        if (!name.trim()) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Name is required',
            });
            return;
        }

        const offDays = rosters.filter((roster) => roster.dayType === 'off day').length;
        const morningHalfDays = rosters.filter((roster) => roster.dayType === 'morning half day').length;
        const eveningHalfDays = rosters.filter((roster) => roster.dayType === 'evening half day').length;

        if (rosters.length > 0) {
            if (offDays !== 1) {
                Swal.fire({
                    icon: 'error',
                    title: 'Work Schedule Error',
                    text: 'You must have exactly one off day in your work schedule',
                });
                return;
            }

            if ((morningHalfDays === 0 && eveningHalfDays === 0) || morningHalfDays > 1 || eveningHalfDays > 1) {
                Swal.fire({
                    icon: 'error',
                    title: 'Work Schedule Error',
                    text: 'You must have exactly one morning half day OR one evening half day in your work schedule',
                });
                return;
            }

            if (morningHalfDays > 0 && eveningHalfDays > 0) {
                Swal.fire({
                    icon: 'error',
                    title: 'Work Schedule Error',
                    text: 'You cannot have both morning half day and evening half day. Choose only one.',
                });
                return;
            }
        }

        setIsUpdatingProfile(true);

        const formData = {
            name,
            username: isSuperAdmin ? username : undefined,
            officeEmail,
            gmail,
            mobileNumber,
            gender,
            title,
            // Backend expects an array; it validates constraints and upserts staff_rosters
            rosters,
        };

        try {
            const resp = await axiosInstance.put('/admin/lecturer/profile', formData);
            if (resp.data?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: resp.data?.message || 'Profile updated successfully',
                    timer: 2000,
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: resp.data?.message || 'Failed to update profile',
                });
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to update profile';
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: msg,
            });
        } finally {
            setIsUpdatingProfile(false);
        }
    };

    const handlePasswordUpdate = async (event) => {
        event.preventDefault();

        if (!currentPassword || !newPassword || !confirmPassword) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'All password fields are required',
            });
            return;
        }

        if (newPassword.length < 6) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'New password must be at least 6 characters long',
            });
            return;
        }

        if (newPassword !== confirmPassword) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'New password and confirm password do not match',
            });
            return;
        }

        setIsUpdatingPassword(true);

        try {
            const resp = await axiosInstance.put('/admin/lecturer/password', {
                current_password: currentPassword,
                new_password: newPassword,
                new_password_confirmation: confirmPassword,
            });
            if (resp.data?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: resp.data?.message || 'Password updated successfully',
                    timer: 2000,
                });
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: resp.data?.message || 'Failed to update password',
                });
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to update password';
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: msg,
            });
        } finally {
            setIsUpdatingPassword(false);
        }
    };

    return (
        <SalesLayout title={pageTitle} currentSection={section}>
            <div className="sales-stagger mx-auto max-w-7xl py-6">
                <div className="relative mb-8 overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 p-8 text-white shadow-xl">
                    <span className="pointer-events-none absolute -right-10 -top-12 h-44 w-44 animate-pulse rounded-full bg-white/10" />
                    <span className="pointer-events-none absolute -bottom-16 right-28 h-36 w-36 animate-pulse rounded-full bg-white/10" style={{ animationDelay: '0.9s' }} />
                    <div className="relative flex items-center gap-4">
                        <div className="flex-shrink-0">
                            {avatarUrl ? (
                                <img
                                    src={avatarUrl}
                                    alt={name}
                                    onError={() => setAvatarUrl(undefined)}
                                    className="h-20 w-20 rounded-full border-4 border-white/30 object-cover shadow-lg"
                                />
                            ) : (
                                <div className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-white/30 bg-white/20 text-2xl font-bold text-white shadow-lg">
                                    {name
                                        ? name
                                            .split(' ')
                                            .map((part) => part[0])
                                            .join('')
                                            .toUpperCase()
                                        : 'S'}
                                </div>
                            )}
                        </div>
                        <div className="flex-1">
                            <h1 className="text-3xl font-bold">{name || 'Sales Profile'}</h1>
                            <p className="mt-1 flex items-center gap-2 text-white/80">
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                {staff?.employeeNumber && `Employee #${staff.employeeNumber} • `}
                                {isSuperAdmin ? 'Administrator' : 'Sales Team Member'}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <div className="lg:col-span-1">
                        <div className="overflow-hidden rounded-xl border border-sky-100 bg-white shadow-lg">
                            <div className="border-b border-sky-100 bg-gradient-to-br from-sky-50 to-slate-50 px-6 py-4">
                                <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                                    <svg className="h-5 w-5 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                    </svg>
                                    Profile Picture
                                </h2>
                            </div>
                            <div className="p-6">
                                <div className="flex flex-col items-center">
                                    {avatarLoading ? (
                                        <div className="flex h-40 w-40 items-center justify-center rounded-full bg-gradient-to-br from-slate-100 to-slate-200 shadow-inner">
                                            <svg className="h-10 w-10 animate-spin text-sky-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V2C6.477 2 2 6.477 2 12h2zm2 5.291A7.962 7.962 0 014 12H2c0 3.042 1.135 5.824 3 7.938l1-2.647z"></path>
                                            </svg>
                                        </div>
                                    ) : avatarUrl ? (
                                        <div className="group relative">
                                            <img
                                                src={avatarUrl}
                                                alt={name}
                                                onError={() => setAvatarUrl(undefined)}
                                                className="h-40 w-40 rounded-full border-4 border-sky-100 object-cover shadow-xl transition-all group-hover:border-sky-200"
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                                                <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                                </svg>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex h-40 w-40 items-center justify-center rounded-full border-4 border-sky-100 bg-gradient-to-br from-sky-500 to-blue-600 text-5xl font-bold text-white shadow-xl">
                                            {name
                                                ? name
                                                    .split(' ')
                                                    .map((part) => part[0])
                                                    .join('')
                                                    .toUpperCase()
                                                : 'S'}
                                        </div>
                                    )}

                                    <button
                                        onClick={handleImageUploadClick}
                                        className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-sky-600 to-blue-600 px-4 py-3 text-sm font-semibold text-white transition-all hover:from-sky-700 hover:to-blue-700 hover:-translate-y-0.5 hover:shadow-lg"
                                    >
                                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        Change Photo
                                    </button>
                                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-2">
                        <div className="overflow-hidden rounded-xl border border-sky-100 bg-white shadow-lg">
                            <div className="border-b border-sky-100 bg-gradient-to-br from-sky-50 to-slate-50 px-6 py-4">
                                <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                                    <svg className="h-5 w-5 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    Personal Information
                                </h2>
                            </div>
                            <div className="p-8">
                                <form onSubmit={handleProfileUpdate}>
                                    <div className="space-y-6">
                                        <div>
                                            <label htmlFor="name" className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                                                <svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                                Full Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="name"
                                                value={name}
                                                onChange={(event) => setName(event.target.value)}
                                                className="block w-full rounded-lg border-slate-300 px-4 py-3 text-base shadow-sm transition-colors focus:border-sky-500 focus:ring-sky-500"
                                                placeholder="Enter your full name"
                                                required
                                            />
                                        </div>

                                        {isSuperAdmin && (
                                            <div>
                                                <label htmlFor="username" className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                                                    <svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                                    </svg>
                                                    Username <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="username"
                                                    value={username}
                                                    onChange={(event) => setUsername(event.target.value)}
                                                    className="block w-full rounded-lg border-slate-300 px-4 py-3 text-base shadow-sm transition-colors focus:border-sky-500 focus:ring-sky-500"
                                                    placeholder="Enter your username"
                                                    required
                                                />
                                            </div>
                                        )}

                                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                            <div>
                                                <label htmlFor="officeEmail" className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                                                    <svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                    </svg>
                                                    Office Email
                                                </label>
                                                <input
                                                    type="email"
                                                    id="officeEmail"
                                                    value={officeEmail}
                                                    onChange={(event) => setOfficeEmail(event.target.value)}
                                                    className="block w-full rounded-lg border-slate-300 px-4 py-3 text-base shadow-sm transition-colors focus:border-sky-500 focus:ring-sky-500"
                                                    placeholder="office@example.com"
                                                />
                                            </div>

                                            <div>
                                                <label htmlFor="gmail" className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                                                    <svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                                                    </svg>
                                                    Personal Email
                                                </label>
                                                <input
                                                    type="email"
                                                    id="gmail"
                                                    value={gmail}
                                                    onChange={(event) => setGmail(event.target.value)}
                                                    className="block w-full rounded-lg border-slate-300 px-4 py-3 text-base shadow-sm transition-colors focus:border-sky-500 focus:ring-sky-500"
                                                    placeholder="personal@gmail.com"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                            <div>
                                                <label htmlFor="mobileNumber" className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                                                    <svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                    </svg>
                                                    Mobile Number
                                                </label>
                                                <input
                                                    type="tel"
                                                    id="mobileNumber"
                                                    value={mobileNumber}
                                                    onChange={(event) => setMobileNumber(event.target.value)}
                                                    className="block w-full rounded-lg border-slate-300 px-4 py-3 text-base shadow-sm transition-colors focus:border-sky-500 focus:ring-sky-500"
                                                    placeholder="+94 77 000 0000"
                                                />
                                            </div>

                                            <div>
                                                <label htmlFor="gender" className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                                                    <svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                                    </svg>
                                                    Gender
                                                </label>
                                                <select
                                                    id="gender"
                                                    value={gender}
                                                    onChange={(event) => setGender(event.target.value)}
                                                    className="block w-full rounded-lg border-slate-300 px-4 py-3 text-base shadow-sm transition-colors focus:border-sky-500 focus:ring-sky-500"
                                                >
                                                    <option value="">Select Gender</option>
                                                    <option value="male">Male</option>
                                                    <option value="female">Female</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="title" className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                                                <svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                </svg>
                                                Title
                                            </label>
                                            <select
                                                id="title"
                                                value={title}
                                                onChange={(event) => setTitle(event.target.value)}
                                                className="block w-full rounded-lg border-slate-300 px-4 py-3 text-base shadow-sm transition-colors focus:border-sky-500 focus:ring-sky-500"
                                            >
                                                <option value="">Select Title</option>
                                                <option value="Mr">Mr</option>
                                                <option value="Mrs">Mrs</option>
                                                <option value="Miss">Miss</option>
                                                <option value="Ms">Ms</option>
                                                <option value="Dr">Dr</option>
                                            </select>
                                        </div>

                                        <div className="mt-6 border-t-2 border-slate-200 pt-6">
                                            <div className="mb-4 flex items-center gap-2">
                                                <svg className="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                                <h3 className="text-base font-semibold text-slate-900">Weekly Schedule</h3>
                                            </div>
                                            <div className="space-y-2.5 rounded-lg bg-slate-50 p-4">
                                                {DAYS_OF_WEEK.map((day) => {
                                                    const roster = rosters.find((entry) => entry.rosterDay === day);
                                                    const dayType = roster?.dayType || '';

                                                    let bgColor = 'bg-white';
                                                    let borderColor = 'border-slate-200';
                                                    let badgeColor = 'bg-slate-100 text-slate-600';

                                                    if (dayType === 'off day') {
                                                        bgColor = 'bg-rose-50';
                                                        borderColor = 'border-rose-200';
                                                        badgeColor = 'bg-rose-100 text-rose-700';
                                                    } else if (dayType === 'morning half day') {
                                                        bgColor = 'bg-sky-50';
                                                        borderColor = 'border-sky-200';
                                                        badgeColor = 'bg-sky-100 text-sky-700';
                                                    } else if (dayType === 'evening half day') {
                                                        bgColor = 'bg-indigo-50';
                                                        borderColor = 'border-indigo-200';
                                                        badgeColor = 'bg-indigo-100 text-indigo-700';
                                                    }

                                                    return (
                                                        <div key={day} className={`flex items-center gap-4 rounded-lg border-2 p-3 transition-all ${bgColor} ${borderColor}`}>
                                                            <div className="flex w-32 items-center gap-2">
                                                                <div className="h-2 w-2 rounded-full bg-slate-600"></div>
                                                                <span className="text-sm font-semibold text-slate-700">{day}</span>
                                                            </div>
                                                            <div className="flex-1">
                                                                <select
                                                                    value={dayType}
                                                                    onChange={(event) => {
                                                                        const selectedType = event.target.value;
                                                                        let nextRosters = [...rosters];

                                                                        if (selectedType === 'off day') {
                                                                            nextRosters = nextRosters.filter((entry) => entry.rosterDay === day || entry.dayType !== 'off day');
                                                                        } else if (selectedType === 'morning half day') {
                                                                            nextRosters = nextRosters.filter((entry) => entry.rosterDay === day || (entry.dayType !== 'morning half day' && entry.dayType !== 'evening half day'));
                                                                        } else if (selectedType === 'evening half day') {
                                                                            nextRosters = nextRosters.filter((entry) => entry.rosterDay === day || (entry.dayType !== 'evening half day' && entry.dayType !== 'morning half day'));
                                                                        }

                                                                        nextRosters = nextRosters.filter((entry) => entry.rosterDay !== day);

                                                                        if (selectedType) {
                                                                            nextRosters.push({
                                                                                rosterId: roster?.rosterId || 0,
                                                                                rosterDay: day,
                                                                                dayType: selectedType,
                                                                            });
                                                                        }

                                                                        setRosters(nextRosters);
                                                                    }}
                                                                    className="w-full rounded-lg border-slate-300 px-4 py-3 text-base font-medium shadow-sm transition-colors focus:border-sky-500 focus:ring-sky-500"
                                                                >
                                                                    <option value="">🟢 Full Day</option>
                                                                    <option value="morning half day">🌅 Morning Half Day</option>
                                                                    <option value="evening half day">🌆 Evening Half Day</option>
                                                                    <option value="off day">🔴 Off Day</option>
                                                                </select>
                                                            </div>
                                                            {dayType && (
                                                                <span className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ${badgeColor}`}>
                                                                    {dayType === 'off day' ? '🔴 Off' : dayType === 'morning half day' ? '🌅 AM' : dayType === 'evening half day' ? '🌆 PM' : ''}
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <div className="mt-3 flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-slate-600">
                                                <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <p>
                                                    <strong>Note:</strong> Select exactly one off day and one half day (morning or evening) per week so the schedule stays valid.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-8 flex gap-3">
                                        <button
                                            type="submit"
                                            disabled={isUpdatingProfile}
                                            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-sky-600 to-blue-600 px-6 py-3 text-sm font-semibold text-white transition-all hover:from-sky-700 hover:to-blue-700 hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            {isUpdatingProfile ? (
                                                <>
                                                    <svg className="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V2C6.477 2 2 6.477 2 12h2zm2 5.291A7.962 7.962 0 014 12H2c0 3.042 1.135 5.824 3 7.938l1-2.647z"></path>
                                                    </svg>
                                                    Updating...
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    Save Changes
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        <div className="mt-6 overflow-hidden rounded-xl border border-sky-100 bg-white shadow-lg">
                            <div className="border-b border-sky-100 bg-gradient-to-br from-sky-50 to-slate-50 px-6 py-4">
                                <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                                    <svg className="h-5 w-5 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                    Security Settings
                                </h2>
                            </div>
                            <div className="p-8">
                                <form onSubmit={handlePasswordUpdate}>
                                    <div className="space-y-6">
                                        <div>
                                            <label htmlFor="currentPassword" className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                                                <svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                                </svg>
                                                Current Password <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="password"
                                                id="currentPassword"
                                                value={currentPassword}
                                                onChange={(event) => setCurrentPassword(event.target.value)}
                                                className="block w-full rounded-lg border-slate-300 px-4 py-3 text-base shadow-sm transition-colors focus:border-sky-500 focus:ring-sky-500"
                                                placeholder="Enter current password"
                                            />
                                        </div>

                                        <div>
                                            <label htmlFor="newPassword" className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                                                <svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                                </svg>
                                                New Password <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="password"
                                                id="newPassword"
                                                value={newPassword}
                                                onChange={(event) => setNewPassword(event.target.value)}
                                                className="block w-full rounded-lg border-slate-300 px-4 py-3 text-base shadow-sm transition-colors focus:border-sky-500 focus:ring-sky-500"
                                                placeholder="Enter new password"
                                            />
                                            <p className="mt-2 flex items-center gap-1 text-xs text-slate-500">
                                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                Minimum 6 characters required
                                            </p>
                                        </div>

                                        <div>
                                            <label htmlFor="confirmPassword" className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                                                <svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                Confirm New Password <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="password"
                                                id="confirmPassword"
                                                value={confirmPassword}
                                                onChange={(event) => setConfirmPassword(event.target.value)}
                                                className="block w-full rounded-lg border-slate-300 px-4 py-3 text-base shadow-sm transition-colors focus:border-sky-500 focus:ring-sky-500"
                                                placeholder="Re-enter new password"
                                            />
                                        </div>
                                    </div>

                                    <div className="mt-8">
                                        <button
                                            type="submit"
                                            disabled={isUpdatingPassword}
                                            className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-rose-500 to-pink-600 px-6 py-3 text-sm font-semibold text-white transition-all hover:from-rose-600 hover:to-pink-700 hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            {isUpdatingPassword ? (
                                                <>
                                                    <svg className="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V2C6.477 2 2 6.477 2 12h2zm2 5.291A7.962 7.962 0 014 12H2c0 3.042 1.135 5.824 3 7.938l1-2.647z"></path>
                                                    </svg>
                                                    Updating Password...
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                    </svg>
                                                    Update Password
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </SalesLayout>
    );
}
